import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface AgentSettingsRow {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  license_number: string | null;
  license_states: string[] | null;
  agent_status: string;
}

export const Route = createFileRoute("/agent/settings")({
  head: () => ({ meta: [{ title: "Settings — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentSettings />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentSettings() {
  const { user } = useAuth();
  const [row, setRow] = useState<AgentSettingsRow | null>(null);

  useEffect(() => {
    if (!user) return;
    void loadSettings();
  }, [user?.id]);

  async function loadSettings() {
    if (!user) return;
    const [profileRes, agentRes] = await Promise.all([
      supabase.from("profiles").select("full_name, email, phone, status").eq("id", user.id).maybeSingle(),
      supabase.from("agents").select("license_number, license_states, status").eq("user_id", user.id).maybeSingle(),
    ]);
    setRow({
      full_name: profileRes.data?.full_name ?? null,
      email: profileRes.data?.email ?? null,
      phone: profileRes.data?.phone ?? null,
      status: profileRes.data?.status ?? "pending",
      license_number: agentRes.data?.license_number ?? null,
      license_states: agentRes.data?.license_states ?? [],
      agent_status: agentRes.data?.status ?? "pending",
    });
  }

  return (
    <PortalPage eyebrow="Account" title="Settings" intro="Your strategist profile, licensing, and portal status from agency records.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InfoCard title="Profile" rows={[
          ["Name", row?.full_name ?? "—"],
          ["Email", row?.email ?? user?.email ?? "—"],
          ["Phone", row?.phone ?? "—"],
          ["Profile status", row?.status ?? "—"],
        ]} />
        <InfoCard title="Licensing" rows={[
          ["Strategist status", row?.agent_status ?? "—"],
          ["License number", row?.license_number ?? "—"],
          ["Licensed states", row?.license_states?.length ? row.license_states.join(", ") : "—"],
        ]} />
      </div>
    </PortalPage>
  );
}

function InfoCard({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
      <h3 className="font-display text-navy-800 text-[22px]">{title}</h3>
      <dl className="mt-5 divide-y divide-bronze-600/15">
        {rows.map(([label, value]) => (
          <div key={label} className="py-3 flex items-center justify-between gap-4">
            <dt className="font-sans text-[12px] uppercase tracking-[0.16em] text-charcoal-500">{label}</dt>
            <dd className="font-sans text-[14px] text-navy-800 text-right">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}